import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form } from '@dashboard/components/Formik';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';

export default function TicketGroupLayoutForm({ ticketGroup, onClose }) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { onDirty } = useTicketGroupHelpers(ticketGroup._id);

  const editTicketGroupMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketGroup?._id
          ? `/sellers/${sellerId}/ticket-groups/${ticketGroup._id}`
          : `/sellers/${sellerId}/ticket-groups`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      inputClassName="text-sm"
      initialValues={{
        layout: {
          type: ticketGroup?.layout?.type || 'list',
          columns: ticketGroup?.layout?.columns || 2,
          collapse: ticketGroup?.layout?.collapse || false,
          alignment: ticketGroup?.layout?.alignment || 'default',
          spacing: ticketGroup?.layout?.spacing || 'comfortable',
          imageSize: ticketGroup?.layout?.imageSize || 'small',
          showTicketGroupName: ticketGroup?.layout?.showTicketGroupName === false ? false : true,
          showTicketTypeName: ticketGroup?.layout?.showTicketTypeName === false ? false : true,
          showTicketName: ticketGroup?.layout?.showTicketName === false ? false : true,
          showTicketPrice: ticketGroup?.layout?.showTicketPrice === false ? false : true,
          showTicketTotalPrice: ticketGroup?.layout?.showTicketTotalPrice === false ? false : true,
          showTicketSaleDateTimezone:
            ticketGroup?.layout?.showTicketSaleDateTimezone === false ? false : true,
        },
      }}
      onSubmit={async (values, actions) => {
        try {
          const data = await editTicketGroupMutation.mutateAsync({
            ...values,
            pageId: page._id,
          });

          actions.resetForm({
            values: {
              layout: {
                type: data.layout.type,
                columns: data.layout.columns,
                collapse: data.layout.collapse,
                alignment: data.layout.alignment,
                spacing: data.layout.spacing,
                imageSize: data.layout.imageSize,
                showTicketGroupName: data.layout.showTicketGroupName,
                showTicketTypeName: data.layout.showTicketTypeName,
                showTicketName: data.layout.showTicketName,
                showTicketPrice: data.layout.showTicketPrice,
                showTicketTotalPrice: data.layout.showTicketTotalPrice,
                showTicketSaleDateTimezone: data.layout.showTicketSaleDateTimezone,
              },
            },
          });

          return {
            data: data,
          };
        } catch (error) {
          return {
            error: error.message,
          };
        }
      }}
      fields={[
        {
          name: 'layout.type',
          label: 'Layout',
          type: 'select',
          options: [
            { label: 'List', value: 'list' },
            { label: 'Grid', value: 'grid' },
          ],
          reduceMargin: true,
        },
        {
          name: 'layout.columns',
          label: 'Columns',
          gap: 'sm',
          reduceMargin: true,
          type: 'select',
          options: [
            { label: '1', value: 1 },
            { label: '2', value: 2 },
            { label: '3', value: 3 },
            { label: '4', value: 4 },
            { label: '5', value: 5 },
            { label: '6', value: 6 },
            { label: '7', value: 7 },
            { label: '8', value: 8 },
            { label: '9', value: 9 },
            { label: '10', value: 10 },
            { label: '11', value: 11 },
            { label: '12', value: 12 },
          ],
          visible: values => values.layout.type === 'grid',
        },
        {
          name: 'layout.collapse',
          label: 'Display as accordion',
          tooltip: 'Useful when you have many options',
          type: 'checkbox',
          reduceMargin: true,
        },
        {
          type: 'columns',
          columns: 2,
          gap: 'sm',
          reduceMargin: true,
          fields: [
            {
              name: 'layout.alignment',
              label: 'Alignment',
              type: 'select',
              options: [
                { label: 'Default', value: 'default' },
                { label: 'Center', value: 'center' },
                { label: 'Stack', value: 'stack' },
              ],
            },
            {
              name: 'layout.spacing',
              label: 'Spacing',
              type: 'select',
              options: [
                { label: 'Comfortable', value: 'comfortable' },
                { label: 'Compact', value: 'compact' },
              ],
            },
          ],
        },
        {
          type: 'columns',
          columns: 2,
          gap: 'sm',
          fields: [
            {
              name: 'layout.imageSize',
              label: 'Image size',
              type: 'select',
              options: [
                { label: 'Small', value: 'small' },
                { label: 'Medium', value: 'medium' },
                { label: 'Large', value: 'large' },
              ],
            },
          ],
          reduceMargin: true,
        },
        {
          name: 'layout.showHide',
          label: 'Show on group',
          type: 'fieldgroup',
          fields: [
            {
              name: 'layout.showTicketGroupName',
              label: 'Ticket group name',
              type: 'checkbox',
              noMargin: true,
            },
            {
              name: 'layout.showTicketTypeName',
              label: 'Ticket type name',
              type: 'checkbox',
            },
            {
              name: 'layout.showTicketPrice',
              label: 'Ticket price',
              type: 'checkbox',
            },
            {
              name: 'layout.showTicketTotalPrice',
              label: 'Ticket total price',
              type: 'checkbox',
            },
            {
              name: 'layout.showTicketSaleDateTimezone',
              label: 'Ticket sale date timezone',
              type: 'checkbox',
            },
          ],
          reduceMargin: true,
        },
      ]}
      cancelButton={{
        label: 'Cancel',
        onClick: onClose,
      }}
      formSubmitAlign="end"
    />
  );
}
